import { useEffect, useRef, useState } from 'react';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetRole from '../../../API/hooks/useGetRole';
import useGetStreamConfig from '../../../API/hooks/videos/useGetStreamConfig';
import S from './LiveVideoStyles';
import { useStreamTimer, formatDuration } from './useStreamTimer';

// ---------- types ----------

export type IvsPlayerProps = {
  deviceId: string;
  cameraId: number;
  deviceStatus: boolean;
  streamVisibilityDuration: number;
};

type IvsVideoProps = {
  playbackUrl: string;
  streamVisibilityDuration: number;
  onChannelGone: () => void;
  onReset: () => void;
  onDurationEnded: () => void;
  onFirstPlay: () => void;
};

// ---------- constants ----------

const STREAM_START_TIMEOUT_MS = 60_000;
const STREAM_RETRY_INTERVAL_MS = 3_000;

// ---------- IVS CDN player global types ----------

// window.IVSPlayer is injected by the CDN script in index.html.
// Typed minimally to what we need — avoids a dev-dependency on @types/amazon-ivs-player.
declare global {
  interface Window {
    IVSPlayer: {
      isPlayerSupported: boolean;
      create: () => IVSPlayerInstance;
      PlayerState: Record<string, string>;
      PlayerEventType: Record<string, string>;
      ErrorType: { NOT_AVAILABLE: string };
    };
  }
}

interface IVSQuality {
  name: string;
  height: number;
  bitrate: number;
}

interface IVSPlayerError {
  type: string;
  code: number;
  message: string;
}

interface IVSPlayerInstance {
  attachHTMLVideoElement: (el: HTMLVideoElement) => void;
  load: (url: string) => void;
  play: () => void;
  pause: () => void;
  delete: () => void;
  getQualities: () => IVSQuality[];
  getQuality: () => IVSQuality;
  setQuality: (q: IVSQuality) => void;
  addEventListener: (event: string, cb: (payload?: any) => void) => void;
  removeEventListener: (event: string, cb: (payload?: any) => void) => void;
}

// ---------- video player ----------

// The <video> element must stay in the DOM at all times — the IVS player
// attaches to it once on mount. Visibility is toggled via CSS so the player
// always has a valid element to write frames to.
function IvsVideo({
  playbackUrl, streamVisibilityDuration, onChannelGone, onReset, onDurationEnded, onFirstPlay,
}: Readonly<IvsVideoProps>): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { canPlayVideoAudio } = useGetRole();
  const [status, setStatus] = useState<'waiting' | 'buffering' | 'playing' | 'ended' | 'error'>('waiting');
  const [everPlayed, setEverPlayed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [qualities, setQualities] = useState<IVSQuality[]>([]);
  const [activeQuality, setActiveQuality] = useState<IVSQuality | null>(null);
  const playerRef = useRef<IVSPlayerInstance | null>(null);
  const durationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamVisibilityDurationRef = useRef(streamVisibilityDuration);
  useEffect(() => { streamVisibilityDurationRef.current = streamVisibilityDuration; }, [streamVisibilityDuration]);

  useEffect(() => {
    const { IVSPlayer } = window;

    if (!IVSPlayer?.isPlayerSupported) {
      setStatus('error');
      setErrorMessage('IVS player is not supported in this browser.');
      return () => {};
    }
    if (!videoRef.current) return () => {};

    const player = IVSPlayer.create();
    playerRef.current = player;
    player.attachHTMLVideoElement(videoRef.current);

    const { PlayerState, PlayerEventType, ErrorType } = IVSPlayer;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let totalWaited = 0;

    const tryLoad = (): void => {
      player.load(playbackUrl);
      player.play();
    };

    const scheduleRetry = (): void => {
      totalWaited += STREAM_RETRY_INTERVAL_MS;
      if (totalWaited >= STREAM_START_TIMEOUT_MS) {
        setStatus('error');
        setErrorMessage('Device did not start streaming in time. Please try again.');
        return;
      }
      retryTimer = setTimeout(tryLoad, STREAM_RETRY_INTERVAL_MS);
    };

    const onBuffering = (): void => setStatus('buffering');

    const onPlaying = (): void => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (!durationTimeoutRef.current) {
        onFirstPlay();
        durationTimeoutRef.current = setTimeout(() => {
          onDurationEnded();
        }, streamVisibilityDurationRef.current);
      }
      setEverPlayed(true);
      setStatus('playing');
    };

    const onEnded = (): void => {
      setStatus('ended');
      onChannelGone();
    };

    const onReady = (): void => {
      const qs = player.getQualities();
      setQualities(qs);
      setActiveQuality(player.getQuality());
    };

    const onQualityChanged = (q: IVSQuality): void => setActiveQuality(q);

    const onError = (err: IVSPlayerError): void => {
      console.error('[IvsPlayer] error:', err);

      // NOT_AVAILABLE + 404: stream not live yet — keep retrying silently
      if (err.type === ErrorType.NOT_AVAILABLE && err.code === 404) {
        scheduleRetry();
        return;
      }

      // Any other error (channel deleted, auth, network) — surface it and
      // reset the parent to the start screen
      setStatus('error');
      setErrorMessage(`Stream unavailable (${err.code}). Please try again.`);
      onChannelGone();
    };

    player.addEventListener(PlayerState.BUFFERING, onBuffering);
    player.addEventListener(PlayerState.PLAYING, onPlaying);
    player.addEventListener(PlayerState.ENDED, onEnded);
    player.addEventListener(PlayerState.READY, onReady);
    player.addEventListener(PlayerEventType.QUALITY_CHANGED, onQualityChanged);
    player.addEventListener(PlayerEventType.ERROR, onError);

    tryLoad();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (durationTimeoutRef.current) {
        clearTimeout(durationTimeoutRef.current);
        durationTimeoutRef.current = null;
      }
      player.removeEventListener(PlayerState.BUFFERING, onBuffering);
      player.removeEventListener(PlayerState.PLAYING, onPlaying);
      player.removeEventListener(PlayerState.ENDED, onEnded);
      player.removeEventListener(PlayerState.READY, onReady);
      player.removeEventListener(PlayerEventType.QUALITY_CHANGED, onQualityChanged);
      player.removeEventListener(PlayerEventType.ERROR, onError);
      player.pause();
      player.delete();
      playerRef.current = null;
    };
  }, [playbackUrl]);

  useEffect(() => {
    if (!durationTimeoutRef.current) return;
    clearTimeout(durationTimeoutRef.current);
    durationTimeoutRef.current = setTimeout(() => {
      onDurationEnded();
    }, streamVisibilityDuration);
  }, [streamVisibilityDuration]);

  const handleQualityChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selected = qualities.find((q) => q.name === e.target.value);
    if (selected && playerRef.current) {
      playerRef.current.setQuality(selected);
      setActiveQuality(selected);
    }
  };

  return (
    <div className="video-stage">
      <video
        className="live-video"
        ref={videoRef}
        playsInline
        autoPlay
        muted={!canPlayVideoAudio}
        style={{ visibility: everPlayed ? 'visible' : 'hidden' }}
      >
        <track kind="captions" src="" label="English" />
      </video>

      {!everPlayed && status === 'waiting' && (
        <div className="video-overlay">
          <span>Waiting for device to start streaming...</span>
        </div>
      )}

      {status === 'buffering' && (
        <div className="video-overlay buffering-overlay">
          <span>Buffering...</span>
        </div>
      )}

      {(status === 'error' || status === 'ended') && (
        <div className="video-overlay">
          <span>{status === 'ended' ? 'Stream ended.' : errorMessage}</span>
          <button type="button" onClick={onReset}>Start new stream</button>
        </div>
      )}

      {qualities.length > 1 && status === 'playing' && (
        <div className="quality-bar">
          <select value={activeQuality?.name ?? ''} onChange={handleQualityChange}>
            {qualities.map((q) => (
              <option key={q.name} value={q.name}>
                {q.name} ({q.height}p)
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ---------- IVS player shell (fetch config → show video) ----------

// Fetches the same /credentials endpoint as WebRTC. Backend returns different
// fields (playbackUrl instead of channelArn + AWS keys) when liveStreamProtocol
// is srt or rtmp.
function IvsPlayer({
  deviceId, cameraId, deviceStatus, streamVisibilityDuration,
}: Readonly<IvsPlayerProps>): JSX.Element {
  const fleetId = useGetFleetId();
  const [started, setStarted] = useState(false);
  const [durationEnded, setDurationEnded] = useState(false);
  const { data: streamConfig, isLoading } = useGetStreamConfig(deviceId, fleetId, cameraId, false, started);
  const { state: timerState, startTimer, stopTimer } = useStreamTimer(streamVisibilityDuration);

  const shellOverlay = (message: string, action?: JSX.Element): JSX.Element => (
    <S.VideoContainer>
      <div className="video-stage-placeholder">
        <span>{message}</span>
        {action}
      </div>
    </S.VideoContainer>
  );

  if (deviceStatus === false) {
    return shellOverlay('Device is offline. Please try again when device is back online.');
  }

  if (!started) {
    return shellOverlay(
      'Press "Start" to request live video',
      <button type="button" onClick={() => setStarted(true)}>Start stream</button>,
    );
  }

  if (isLoading) {
    return shellOverlay('Loading stream configuration...');
  }

  if (!streamConfig?.playbackUrl) {
    console.log('[IvsPlayer] stream config received, no playbackUrl:', streamConfig);
    return shellOverlay('Waiting for IVS stream configuration...');
  }

  if (durationEnded) {
    return shellOverlay(
      'Stream duration limit reached. Please try again or change stream duration.',
      <button type="button" onClick={() => { setDurationEnded(false); setStarted(false); }}>Start new stream</button>,
    );
  }

  return (
    <S.VideoContainer>
      <IvsVideo
        playbackUrl={streamConfig.playbackUrl}
        streamVisibilityDuration={streamVisibilityDuration}
        onChannelGone={() => { stopTimer(); setStarted(false); }}
        onReset={() => { stopTimer(); setStarted(false); }}
        onDurationEnded={() => { stopTimer(); setDurationEnded(true); }}
        onFirstPlay={startTimer}
      />
      {timerState.elapsed > 0 && (
        <div className="stream-timer">
          <span>Stream duration: {formatDuration(timerState.elapsed)}</span>
          {timerState.remaining !== null && (
            <span>Remaining: {formatDuration(timerState.remaining)}</span>
          )}
        </div>
      )}
    </S.VideoContainer>
  );
}

export default IvsPlayer;
